import "./style.css";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useObservable } from "react-use-observable";

import { IWatch } from "../../../../../interfaces/watch";
import { audioService } from "../../../../../services/audio";
import AudioControls from "./AudioControls";
import Thumbnail from "./Thumbnail";

interface IAudioPlayerProps {
  watchInfo: IWatch;
}

const Audio: React.FC<IAudioPlayerProps> = ({ watchInfo }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  const [audioURL] = useObservable<string | undefined>(
    () => audioService.getAudioUrl(watchInfo.playlist),
    []
  );

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration);
    }
  }, []);

  const updateEventListeners = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [handleTimeUpdate, handleLoadedMetadata]);

  useEffect(() => {
    const cleanup = updateEventListeners();
    return () => {
      if (cleanup) cleanup();
    };
  }, [audioURL, updateEventListeners]);

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      isPlaying ? audio.pause() : audio.play();
      setIsPlaying((prev) => !prev);
    }
  }, [isPlaying]);

  const handleSeek = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const audio = audioRef.current;
      if (audio) {
        const seekTime = (event.target.valueAsNumber / 100) * duration;
        audio.currentTime = seekTime;
        setCurrentTime(seekTime);
      }
    },
    [duration]
  );

  const handleVolumeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const audio = audioRef.current;
      if (audio) {
        const newVolume = event.target.valueAsNumber / 100;
        audio.volume = newVolume;
        setVolume(newVolume);
        setIsMuted(newVolume === 0);
      }
    },
    []
  );

  const handleMuteUnmute = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !isMuted;
      setIsMuted((prev) => !prev);
    }
  }, [isMuted]);

  const handlePlaybackRateChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const audio = audioRef.current;
      if (audio) {
        const newPlaybackRate = parseFloat(event.target.value);
        audio.playbackRate = newPlaybackRate;
        setPlaybackRate(newPlaybackRate);
      }
    },
    []
  );

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  }, []);

  if (!audioURL) {
    return <></>;
  }

  return (
    <div className="audio-container">
      <Thumbnail thumbnail={watchInfo.thumbnail} />

      <audio ref={audioRef}>
        <source src={audioURL!} type="audio/mp3" />
      </audio>

      <AudioControls
        isPlaying={isPlaying}
        isMuted={isMuted}
        volume={volume}
        playbackRate={playbackRate}
        currentTime={currentTime}
        duration={duration}
        onPlayPause={handlePlayPause}
        onSeek={handleSeek}
        onVolumeChange={handleVolumeChange}
        onMuteUnmute={handleMuteUnmute}
        onPlaybackRateChange={handlePlaybackRateChange}
        formatTime={formatTime}
      />
    </div>
  );
};

export default Audio;
